import * as React from 'react';
import {
  ConditionGroupVO,
  DataSortVO,
  FieldValuesFilterVO,
} from '@mc/data-filter-react';
import { DataAggregateVO, DataGroupVO } from '../final-table/FinalTableProps';
import { FilterType } from '../filter/types';
import { FilterDef } from '../filter/Filters';

export type { ConditionGroupVO };

export type FilterData = {
  initializedFromConfig: boolean;
  formFilter: any;
  queryFilter: any;
  lastFilterChange: number;
  visibleFields?: string[];
  formFilterParam?: any;
  fieldValuesFilter: FieldValuesFilterData;
  formExtendedFilter?: ConditionGroupVO;
  queryExtendedFilter?: ConditionGroupVO;
};
export type RowsData<ID> = {
  initializedFromConfig?: boolean;
  sort: DataSortVO[];
  scrollTop: number;
  columns: ColumnInfo[];
  showFooter: boolean;
  checkedIds: ID[];
  grouping: DataGroupVO[];
  groupColumnWidth?: number;
  aggregate: DataAggregateVO[];
};

export type PaginationData = {
  page: number;
  pageSize: number;
};
export const defaultFilterData: FilterData = {
  initializedFromConfig: false,
  formFilter: {},
  queryFilter: {},
  visibleFields: undefined,
  formFilterParam: undefined,
  lastFilterChange: new Date().getMilliseconds(),
  fieldValuesFilter: {},
  formExtendedFilter: undefined,
  queryExtendedFilter: undefined,
};

export const defaultRowsData: RowsData<any> = {
  sort: [],
  scrollTop: 0,
  columns: [],
  checkedIds: [],
  grouping: [],
  aggregate: [],
  showFooter: false,
};

export const defaultPaginationData: PaginationData = {
  page: 1,
  pageSize: 1000,
};

export type DataMapItem = {
  filterData: FilterData;
  rowsData: RowsData<any>;
  paginationData: PaginationData;
};

export type DataMap = {
  [key: string]: DataMapItem;
};
export type LastVisitedRowIndexMap = {
  [key: string]: any;
};

export type RowsDataContextData<ID> = {
  dataMap: DataMap;
  dataMapLastVisitedRowIndex: LastVisitedRowIndexMap;
  setShowFooter: (key: string, showFooter: boolean) => void;
  setFilter: (key: string, formFilter: any, queryFilter: any) => void;
  setExtendedFilter: (
    key: string,
    formExtendedFilter: ConditionGroupVO | undefined,
    queryExtendedFilter: ConditionGroupVO | undefined
  ) => void;
  setFieldValuesFilter: (
    key: string,
    fieldValuesFilter: FieldValuesFilterData
  ) => void;
  setColumns: (key: string, columns: ColumnInfo[]) => void;
  setVisibleFields: (key: string, fields?: string[]) => void;
  setCheckedIds: (key: string, ids: ID[]) => void;
  setScrollTop: (key: string, scrollTop: number) => void;
  setSort: (key: string, sort: DataSortVO[]) => void;
  setPage: (key: string, page: number) => void;
  setLastVisitedRowIndex: (key: string, id: ID) => void;
  setGrouping: (key: string, grouping: DataGroupVO[]) => void;
  setAggregate: (key: string, aggregate: DataAggregateVO[]) => void;
  setGroupColumnWidth: (key: string, groupWidth: number) => void;
  initByProvider: boolean;
};

export const RowsDataContext = React.createContext<RowsDataContextData<any>>({
  dataMap: {},
  dataMapLastVisitedRowIndex: {},
  setShowFooter: () => {},
  setFilter: () => {},
  setExtendedFilter: () => {},
  setFieldValuesFilter: () => {},
  setColumns: () => {},
  setVisibleFields: () => {},
  setCheckedIds: () => {},
  setScrollTop: () => {},
  setSort: () => {},
  setPage: () => {},
  setLastVisitedRowIndex: () => {},
  initByProvider: false,
  setGrouping: () => {},
  setAggregate: () => {},
  setGroupColumnWidth: () => {},
});

export type FiltersMap = { [key: string]: FilterData };
export type RowsMap<ID> = { [key: string]: RowsData<ID> };

export type AreaKey = string | readonly unknown[];

export const getAreaKey = (filterKey: AreaKey) => {
  let key: string;
  if (Array.isArray(filterKey)) {
    key = filterKey.join('|');
  } else {
    key = `${filterKey}`;
  }
  return key;
};

export type RowsDataWatchProps<ID> = {
  rows: {
    checkedIds: ID[];
    setCheckedIds: (ids: ID[]) => void;
  };
};

export type FieldValuesFilterData = { [key: string]: FieldValuesFilterVO };

export type ClearFilterConfig = {
  /**
   * Pokud je true, tak se vrátí initial filtr, pokud false, tak prázdný filtr ale s hodnotami které mají allowNull false
   */
  initialFilter: boolean;
  type?: FilterType;
  filtersDef: FilterDef[];
};

export type RowsDataUseProps<FILTER, ID> = {
  filter: {
    formFilter: FILTER;
    queryFilter: FILTER;
    fieldValuesFilter: FieldValuesFilterData;
    formExtendedFilter?: ConditionGroupVO;
    queryExtendedFilter?: ConditionGroupVO;
    lastFilterChange: number;
    initialVisibleFields?: string[];
    visibleFields?: string[];
    setFilter: (filter: FILTER, queryData: FILTER) => void;
    setExtendedFilter: (formExtendedFilter: ConditionGroupVO | undefined) => void;
    getClearFilter: (config: ClearFilterConfig) => any;
    setFieldValuesFilter: (fieldValuesFilter: FieldValuesFilterData) => void;
    setVisibleFields: (fields?: string[]) => void;
  };
  // column: {
  //   visibleColumns: string[];
  //   setVisibleColumns: (visibleColumns: string[]) => void;
  // };
  footer: {
    showFooter: boolean;
    setShowFooter: (showFooter: boolean) => void;
  };
  grouping: {
    grouping: DataGroupVO[];
    setGrouping: (grouping: DataGroupVO[]) => void;
    aggregate: DataAggregateVO[];
    setAggregate: (aggregate: DataAggregateVO[]) => void;
    setGroupColumnWidth: (groupWidth: number) => void;
    groupColumnWidth?: number;
  };
  rows: {
    checkedIds: ID[];
    setCheckedIds: (ids: ID[]) => void;
    sort: DataSortVO[];
    setSort: (sort: DataSortVO[]) => void;
    scrollTop: number;
    lastVisitedRowIndex?: number;
    setLastVisitedRowIndex: (index: number) => void;
    setScrollTop: (scrollTop: number) => void;
  };
  columns: {
    columns: ColumnInfo[];
    setColumns: (columns: ColumnInfo[]) => void;
  };
  pagination: {
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
  };
};
export type ColumnInfo = {
  /**
   * Pixel šířka sloupce, buď číslo (ať už number nebo string) jako počet pixelů
   */
  width?: number;
  /**
   * Identifikátor sloupce.
   */
  ident: string;
  visible: boolean;
};
export type BaseDataTableProps<ID> = {
  checkedIds: ID[];
  setCheckedIds: (ids: ID[]) => void;
  lastVisitedRowIndex?: number;
  setLastVisitedRowIndex: (index: number) => void;
  sort: DataSortVO[];
  onSort: (sort: DataSortVO[]) => void;
};
export type BaseVirtualListProps<ID> = {
  checkedIds: ID[];
  setCheckedIds: (ids: ID[]) => void;
};
export type BasePaginationTableProps<ID> = {
  checkedIds: ID[];
  setCheckedIds: (ids: ID[]) => void;
  lastVisitedRowIndex?: number;
  setLastVisitedRowIndex: (index: number) => void;
  sort: DataSortVO[];
  onSort: (sort: DataSortVO[]) => void;
  page: number;
  onPageChange: (page: number) => void;
  pageSize: number;
};
